import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { appeler } from "../../api/client";
import { AvisSchema, fcfa, MOIS, useApiEnveloppe } from "../../api/hooks";
import { EtatChargement, EtatErreur, ListeAvis } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

const CATEGORIES = [
  "Ouverture", "EncaissementClient", "EntreeAJustifier", "AchatFournisseur",
  "Salaire", "CotisationSociale", "ImpotTaxe", "FraisBancaires", "Dirigeant",
  "SortieAJustifier", "RejetImpaye", "VirementInterne", "Autre",
] as const;

const OperationSchema = z.object({
  index: z.number(),
  date: z.string(),
  libelle: z.string(),
  debit: z.number(),
  credit: z.number(),
  soldeApres: z.number().nullable(),
  categorie: z.string(),
  categorieLibelle: z.string(),
  forcee: z.boolean(),
  observation: z.string().nullable(),
  compteDeTiers: z.string(),
});

const AnalyseSchema = z.array(
  z.object({
    banqueId: z.string(),
    corrections: z.array(z.string()),
    analyse: z
      .object({
        soldeOuverture: z.number(),
        soldeCloture: z.number(),
        totalDebits: z.number(),
        totalCredits: z.number(),
        clotureCalculee: z.number(),
        boucle: z.boolean(),
        ecartDeBouclage: z.number(),
        synthese: z.array(
          z.object({
            categorie: z.string(),
            code: z.string(),
            nombre: z.number(),
            debits: z.number(),
            credits: z.number(),
          }),
        ),
        operations: z.array(OperationSchema),
      })
      .nullable(),
    avis: z.array(AvisSchema),
  }),
);

const ExpositionSchema = z.object({
  postes: z.array(
    z.object({
      code: z.string(),
      libelle: z.string(),
      baseExposee: z.number(),
      gravite: z.string(),
      risque: z.string(),
      impotEstime: z.number(),
      reference: z.string().nullable(),
      alternative: z.string().nullable(),
      impotAlternatif: z.number().nullable(),
    }),
  ),
  totalBaseExposee: z.number(),
  totalImpotEstime: z.number(),
});

export function AnalyseReleve({ dossierId, annee, mois }: EcranProps) {
  const qc = useQueryClient();
  const [seuil, setSeuil] = useState(100_000);

  const analyse = useApiEnveloppe(
    ["releve", dossierId, annee, mois],
    `/dossiers/${dossierId}/releves/${annee}/${mois}`,
    AnalyseSchema,
  );

  const exposition = useApiEnveloppe(
    ["exposition", dossierId, annee, mois],
    `/dossiers/${dossierId}/releves/${annee}/${mois}/exposition`,
    ExpositionSchema,
  );

  async function arbitrer(releveId: string, index: number, categorie: string) {
    await appeler(
      `/dossiers/${dossierId}/releves/${releveId}/categorie`,
      z.object({ ancienne: z.string(), nouvelle: z.string(), compteDeTiers: z.string(), note: z.string() }),
      { method: "PUT", body: JSON.stringify({ indexOperation: index, categorie, observation: null }) },
    );
    void qc.invalidateQueries({ queryKey: ["releve", dossierId, annee, mois] });
    void qc.invalidateQueries({ queryKey: ["exposition", dossierId, annee, mois] });
  }

  if (analyse.isLoading) return <EtatChargement quoi="du relevé" />;
  if (analyse.error) return <EtatErreur erreur={analyse.error} />;

  const comptes = analyse.data?.valeur ?? [];

  if (comptes.length === 0)
    return (
      <div>
        <p className="a-faire">
          Aucun relevé importé pour {MOIS[mois - 1]} {annee}.
        </p>
        <ListeAvis avis={analyse.data?.avis ?? []} />
      </div>
    );

  return (
    <div>
      <p className="resume">
        Les relevés sont lus <strong>là où ils sont déjà archivés</strong>. Chaque
        mois est enregistré avec son propre bouclage, et c'est la{" "}
        <strong>date de l'opération</strong> qui décide de son mois — jamais le nom
        du dossier.
      </p>

      {comptes.map((c) =>
        c.analyse === null ? (
          <section key={c.banqueId}>
            <h2>Compte {c.banqueId.slice(0, 8)}</h2>
            <ListeAvis avis={c.avis} titre="Lecture refusée" />
          </section>
        ) : (
          <section key={c.banqueId}>
            <h2>Compte {c.banqueId.slice(0, 8)}</h2>

            <Tuiles
              tuiles={[
                { titre: "Solde d'ouverture", valeur: fcfa(c.analyse.soldeOuverture) },
                { titre: "Total crédits", valeur: fcfa(c.analyse.totalCredits) },
                { titre: "Total débits", valeur: fcfa(c.analyse.totalDebits) },
                { titre: "Clôture calculée", valeur: fcfa(c.analyse.clotureCalculee) },
                {
                  titre: "Bouclage",
                  valeur: c.analyse.boucle ? "au franc" : fcfa(c.analyse.ecartDeBouclage),
                  alerte: !c.analyse.boucle,
                },
              ]}
            />

            {c.corrections.length > 0 && (
              <div className="corrections">
                <h3>Corrections par la chaîne des soldes</h3>
                <p className="resume">
                  Le solde est corroboré par toute la chaîne, alors que le montant
                  n'est écrit qu'une fois. Chaque correction est{" "}
                  <strong>nommée en observation</strong>.
                </p>
                <ul>
                  {c.corrections.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            )}

            <h3>Synthèse par catégorie</h3>
            <Tableau
              colonnes={[
                { cle: "c", titre: "Catégorie", rendu: (s) => s.categorie },
                { cle: "n", titre: "Nombre", montant: true, rendu: (s) => s.nombre },
                { cle: "d", titre: "Débits", montant: true, rendu: (s) => fcfa(s.debits) },
                { cle: "cr", titre: "Crédits", montant: true, rendu: (s) => fcfa(s.credits) },
              ]}
              lignes={c.analyse.synthese}
              cle={(s) => s.code}
            />

            <h3>Opérations</h3>
            <p className="resume">
              La catégorie <strong>décide du compte de tiers</strong> : la corriger
              remonte aux écritures. Chaque ligne passe toujours par un tiers —
              seuls les frais bancaires font exception.
            </p>
            <Tableau
              colonnes={[
                { cle: "d", titre: "Date", rendu: (o) => o.date },
                { cle: "l", titre: "Libellé", rendu: (o) => o.libelle },
                { cle: "deb", titre: "Débit", montant: true, rendu: (o) => (o.debit ? fcfa(o.debit) : "") },
                { cle: "cre", titre: "Crédit", montant: true, rendu: (o) => (o.credit ? fcfa(o.credit) : "") },
                {
                  cle: "s",
                  titre: "Solde",
                  montant: true,
                  rendu: (o) => (o.soldeApres === null ? "—" : fcfa(o.soldeApres)),
                },
                {
                  cle: "cat",
                  titre: "Catégorie",
                  rendu: (o) => (
                    <select
                      value={o.categorie}
                      onChange={(e) => void arbitrer(c.banqueId, o.index, e.target.value)}
                      className={o.forcee ? "arbitree" : undefined}
                      title={o.forcee ? "Catégorie arbitrée par un collaborateur" : undefined}
                    >
                      {CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  ),
                },
                {
                  cle: "tiers",
                  titre: "Compte de tiers",
                  rendu: (o) => <code>{o.compteDeTiers}</code>,
                },
                { cle: "obs", titre: "Observation", rendu: (o) => o.observation ?? "—" },
              ]}
              lignes={c.analyse.operations}
              cle={(o) => String(o.index)}
            />

            <ListeAvis avis={c.avis} titre="Observations de lecture" />
          </section>
        ),
      )}

      {exposition.data?.valeur && exposition.data.valeur.postes.length > 0 && (
        <section>
          <h2>Niveau d'exposition au redressement</h2>
          <p className="resume">
            <strong>On affiche le brut ; la minoration se prouve.</strong> Quand la
            donnée ne tranche pas, les deux hypothèses sont chiffrées et nommées.
          </p>

          <Tableau
            colonnes={[
              { cle: "c", titre: "Code", rendu: (p) => <code>{p.code}</code> },
              { cle: "l", titre: "Poste", rendu: (p) => p.libelle },
              { cle: "b", titre: "Base exposée", montant: true, rendu: (p) => fcfa(p.baseExposee) },
              { cle: "g", titre: "Gravité", rendu: (p) => p.gravite },
              { cle: "r", titre: "Risque", rendu: (p) => p.risque },
              { cle: "ref", titre: "Référence", rendu: (p) => p.reference ?? "—" },
              { cle: "i", titre: "Impôt estimé", montant: true, rendu: (p) => fcfa(p.impotEstime) },
            ]}
            lignes={exposition.data.valeur.postes}
            cle={(p) => p.code}
          />

          {exposition.data.valeur.postes
            .filter((p) => p.alternative !== null)
            .map((p) => (
              <div key={p.code} className="hypotheses">
                <strong>{p.libelle}</strong>
                <p>{p.alternative}</p>
              </div>
            ))}

          <Tuiles
            tuiles={[
              { titre: "Base exposée totale", valeur: fcfa(exposition.data.valeur.totalBaseExposee) },
              { titre: "Impôt estimé", valeur: fcfa(exposition.data.valeur.totalImpotEstime) },
            ]}
          />
        </section>
      )}

      <section>
        <h2>Demande d'explication écrite</h2>
        <p className="resume">
          On soumet, on met en attente, <strong>on traite après la réponse — jamais
          l'inverse</strong>. Aucune écriture qualificative n'est passée avant
          réponse du client.
        </p>

        <label className="filtre">
          Seuil de sélection
          <input
            type="text"
            inputMode="numeric"
            value={seuil.toLocaleString("fr-FR")}
            onChange={(e) => setSeuil(Number(e.target.value.replace(/\D/g, "")) || 0)}
          />
          <span className="note">
            Sans seuil : 47 points sur un mois, dont un à 4 F. Noyer un chèque de
            12 M au milieu de frais de 4 F est un échec.
          </span>
        </label>

        <div className="actions">
          <a
            className="bouton"
            href={`/api/dossiers/${dossierId}/releves/${annee}/${mois}/dee?seuil=${seuil}&pdf=true`}
          >
            Générer la DEE (PDF à la charte)
          </a>
        </div>
      </section>
    </div>
  );
}
